import React, { useState, useEffect, useMemo, useCallback } from "react";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import StudentMenu from "./StudentMenu";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { NavLink } from "react-router-dom";
import { useParams } from "react-router-dom";
import moment from "moment";
import Pagination from "../General/Pagination";
import ShowStudent from "../Shared/ShowStudent";
import YearsDropDown from "../Shared/YearsDropDown";
import MonthsDropDown from "../Shared/MonthsDropDown";

function parseMoney(v) {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(n) {
  return parseMoney(n).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** One row per fee code: latest PostingDate (then highest ID). Avoids double-counting when arrears are rolled into a later month's receivable. */
function getLatestRowPerFeeCode(rows) {
  if (!rows || rows.length === 0) return [];
  const byFee = new Map();
  for (const item of rows) {
    const code = String(item.FeeCode ?? "").trim() || "_";
    const existing = byFee.get(code);
    const t = item.PostingDate ? new Date(item.PostingDate).getTime() : 0;
    const id = item.ID != null ? Number(item.ID) : 0;
    if (!existing) {
      byFee.set(code, item);
      continue;
    }
    const et = existing.PostingDate ? new Date(existing.PostingDate).getTime() : 0;
    const exid = existing.ID != null ? Number(existing.ID) : 0;
    if (t > et || (t === et && id > exid)) {
      byFee.set(code, item);
    }
  }
  return Array.from(byFee.values());
}

function sumLedgerRows(rows) {
  return rows.reduce(
    (acc, item) => ({
      receiveable: acc.receiveable + parseMoney(item.ReceiveableFee),
      received: acc.received + parseMoney(item.ReceivedAmount),
      pending: acc.pending + parseMoney(item.PendingFee),
      arrears: acc.arrears + parseMoney(item.Arrears),
    }),
    { receiveable: 0, received: 0, pending: 0, arrears: 0 }
  );
}

function StudentLedger2() {
  const params = useParams();
  const studentIdParam = params.id;

  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [loading, setLoading] = useState(false);

  const [fullLedger, setFullLedger] = useState([]);
  const [filteredLedger, setFilteredLedger] = useState([]);
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterChallanNo, setFilterChallanNo] = useState("");

  const [showLedgers, setShowLedgers] = useState(true);
  const [activeTab, setActiveTab] = useState("adLedger");
  const [challans, setChallans] = useState([]);
  const [loadingChallans, setLoadingChallans] = useState(false);

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const totalRecords = filteredLedger.length;

  const listOfLedger = useMemo(() => {
    return filteredLedger.slice(
      (pageNo - 1) * pageSize,
      pageNo * pageSize
    );
  }, [filteredLedger, pageNo, pageSize]);

  /** Net totals: latest row per fee code so arrears are not counted twice (older months + rolled-in receivable). */
  const totalsNetFiltered = useMemo(() => {
    return sumLedgerRows(getLatestRowPerFeeCode(filteredLedger));
  }, [filteredLedger]);

  const totalsNetAll = useMemo(() => {
    return sumLedgerRows(getLatestRowPerFeeCode(fullLedger));
  }, [fullLedger]);

  const loadLedger = useCallback(() => {
    if (!AccessKey || !UserID || !studentIdParam) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const data = {
      AccessKey,
      UserID,
      MemberType: MemberType || "Admin",
      Student_ID: parseInt(studentIdParam, 10) || 0,
    };

    axios({
      method: "post",
      url: config.base_url + "ADLedger/GetADLedgerOfStudentAll",
      headers: { "Content-Type": "application/json" },
      data: data,
    })
      .then(function (response) {
        if (response.data.status_code === 1) {
          const records = response.data.ListofRecords || [];
          setFullLedger(records);
          setFilteredLedger(records);
          setPageNo(1);
        } else {
          setFullLedger([]);
          setFilteredLedger([]);
          toast.error(response.data.status_message || "No ledger data.", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(function () {
        toast.error("Network Error..", {
          position: toast.POSITION.TOP_RIGHT,
        });
        setFullLedger([]);
        setFilteredLedger([]);
      })
      .finally(() => setLoading(false));
  }, [AccessKey, UserID, MemberType, studentIdParam]);

  const loadChallans = useCallback(() => {
    if (!AccessKey || !UserID || !studentIdParam) {
      return;
    }

    setLoadingChallans(true);
    const data = {
      AccessKey,
      UserID,
      MemberType: MemberType || "Admin",
      Student_ID: parseInt(studentIdParam, 10) || 0,
    };

    axios({
      method: "post",
      url: config.base_url + "Student/GetChallansForStudent",
      headers: { "Content-Type": "application/json" },
      data: data,
    })
      .then(function (response) {
        if (response.data.status_code === 1) {
          setChallans(response.data.ListofRecords || []);
        } else {
          setChallans([]);
        }
      })
      .catch(function () {
        setChallans([]);
      })
      .finally(() => setLoadingChallans(false));
  }, [AccessKey, UserID, MemberType, studentIdParam]);

  useEffect(() => {
    document.title = "View Student Ledger";
    loadLedger();
    loadChallans();
  }, [loadLedger, loadChallans]);

  const handlePageChange = (page) => {
    setPageNo(page);
  };

  const applyFilters = () => {
    let filtered = [...fullLedger];

    if (filterChallanNo && filterChallanNo.trim().length > 0) {
      const challan = filterChallanNo.trim();
      filtered = filtered.filter((item) => item.Challan_No === challan);
    } else if (filterMonth && filterYear) {
      const selectedMonth = parseInt(filterMonth, 10);
      const y = filterYear;
      filtered = filtered.filter((item) => {
        if (!item.PostingDate || !moment(item.PostingDate).isValid()) {
          return false;
        }
        const month = moment(item.PostingDate).month() + 1;
        const year = moment(item.PostingDate).format("YYYY");
        return month === selectedMonth && year === y;
      });
    }

    setPageNo(1);
    setFilteredLedger(filtered);
  };

  const resetFilters = () => {
    setFilterMonth("");
    setFilterYear("");
    setFilterChallanNo("");
    setPageNo(1);
    setFilteredLedger(fullLedger);
  };

  const renderLedger = () => {
    if (listOfLedger != null && listOfLedger.length > 0) {
      return listOfLedger.map((item, index) => (
        <tr key={item.ID != null ? `id-${item.ID}-${index}` : index}>
          <td>{moment(item.PostingDate).format("MMM YYYY")}</td>
          <td>{item.Challan_No}</td>
          <td>{item.FeeCode}</td>
          <td>{item.Fee_Posted}</td>
          <td>{formatMoney(item.ConcessionAmount)}</td>
          <td>{formatMoney(item.ReceiveableFee)}</td>
          <td>{formatMoney(item.Arrears)}</td>
          <td>{item.Description}</td>
          <td>{formatMoney(item.ReceivedAmount)}</td>
          <td>
            {item.ReceivedDate
              ? moment(item.ReceivedDate).format("DD/MM/YYYY")
              : ""}
          </td>
          <td>{formatMoney(item.PendingFee)}</td>
        </tr>
      ));
    }
    return (
      <tr>
        <td colSpan="11">
          <h4
            className="mt-3"
            style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}
          >
            No Data Found
          </h4>
        </td>
      </tr>
    );
  };

  return (
    <>
      <StudentMenu />

      <ToastContainer />

      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/student/studentdashboard">Students</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Student Ledger
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Student Ledger</div>
                <p className="text-muted small mb-0">
                  Fee lines from AD ledger (tblADLedger). Summary figures use the
                  latest row per fee code within the current filter so amounts
                  rolled forward as arrears are not counted twice.
                </p>
              </div>
              <div className="col-md-6">
                <div className="page-right-actions">
                  <button
                    onClick={() => {
                      setShowLedgers((prevIsActive) => !prevIsActive);
                    }}
                    className="button button-white button-icon"
                  >
                    {showLedgers ? "Hide Detail" : "Show Detail"}
                    <i className="ri-filter-3-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`collapse ${showLedgers ? "show" : ""} `}
          id="collapseFilteres"
        >
          <ShowStudent StudentID={studentIdParam} />
        </div>
        <div className="page-content mt-3">
          <div className="container">
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="text-muted small">Total receivable fee</div>
                    <div className="h4 mb-0 text-primary">
                      {loading ? "…" : formatMoney(totalsNetFiltered.receiveable)}
                    </div>
                    <div className="small text-muted mt-1">
                      Latest row per fee code (filtered)
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="text-muted small">Total received fee</div>
                    <div className="h4 mb-0 text-success">
                      {loading ? "…" : formatMoney(totalsNetFiltered.received)}
                    </div>
                    <div className="small text-muted mt-1">
                      Latest row per fee code (filtered)
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="text-muted small">Outstanding (pending)</div>
                    <div className="h4 mb-0 text-warning">
                      {loading ? "…" : formatMoney(totalsNetFiltered.pending)}
                    </div>
                    <div className="small text-muted mt-1">
                      Latest row per fee code (filtered)
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <ul className="nav nav-pills mb-3">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === "adLedger" ? "active" : ""}`}
                      onClick={() => setActiveTab("adLedger")}
                    >
                      AD Ledger View
                    </button>
                  </li>
                  <li className="nav-item ms-2">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === "challan" ? "active" : ""}`}
                      onClick={() => setActiveTab("challan")}
                    >
                      Challan View
                    </button>
                  </li>
                </ul>

                {activeTab === "adLedger" ? (
                  <>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label className="form-label">Filter Month</label>
                    <select
                      className="form-select"
                      value={filterMonth}
                      onChange={(e) => setFilterMonth(e.target.value)}
                    >
                      <option value="">All</option>
                      <MonthsDropDown />
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Filter Year</label>
                    <select
                      className="form-select"
                      value={filterYear}
                      onChange={(e) => setFilterYear(e.target.value)}
                    >
                      <option value="">All</option>
                      <YearsDropDown />
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Challan No</label>
                    <input
                      type="text"
                      className="form-control"
                      value={filterChallanNo}
                      onChange={(e) => setFilterChallanNo(e.target.value)}
                      placeholder="Exact challan number"
                    />
                  </div>
                  <div className="col-md-3 d-flex align-items-end gap-2 flex-wrap">
                    <button
                      type="button"
                      className="button button-primary"
                      onClick={applyFilters}
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      className="button button-light"
                      onClick={resetFilters}
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <table className="table table-theme">
                  <thead>
                    <tr>
                      <th colSpan="11" className="text-end">
                        {totalRecords} row(s) in current filter (page{" "}
                        {pageNo} of{" "}
                        {Math.max(1, Math.ceil(totalRecords / pageSize) || 1)})
                      </th>
                    </tr>
                    <tr>
                      <th>Fee Month</th>
                      <th>Challan No</th>
                      <th>Fee Code</th>
                      <th>Fee Posted</th>
                      <th>Concession Amount</th>
                      <th>Receiveable Fee</th>
                      <th>Arrears</th>
                      <th>Description</th>
                      <th>Received Amount</th>
                      <th>Received Date</th>
                      <th>Pending Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td className="text-center" colSpan="11">
                          <Spinner animation="border" role="status"></Spinner>
                        </td>
                      </tr>
                    ) : (
                      renderLedger()
                    )}
                    {!loading && filteredLedger.length > 0 && (
                      <tr className="table-light">
                        <td colSpan={5} className="text-end small text-muted">
                          <b>
                            Net totals — latest per fee in filter (not sum of all
                            rows)
                          </b>
                        </td>
                        <td>
                          <b>{formatMoney(totalsNetFiltered.receiveable)}</b>
                        </td>
                        <td>
                          <b>{formatMoney(totalsNetFiltered.arrears)}</b>
                        </td>
                        <td></td>
                        <td>
                          <b>{formatMoney(totalsNetFiltered.received)}</b>
                        </td>
                        <td></td>
                        <td>
                          <b>{formatMoney(totalsNetFiltered.pending)}</b>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {totalRecords > pageSize && (
                  <div className="pagination-wrap">
                    <div className="row align-items-center">
                      <div className="col-md-2 form_default">
                        <div className="d-flex align-items-center">
                          <label className="me-2" style={{ fontSize: "15px" }}>
                            Page Size
                          </label>
                          <div className="form-group">
                            <select
                              className="form-select"
                              style={{ width: "100px" }}
                              value={pageSize}
                              onChange={(e) => {
                                setPageNo(1);
                                setPageSize(parseInt(e.target.value, 10));
                              }}
                            >
                              <option value="10">10</option>
                              <option value="20">20</option>
                              <option value="50">50</option>
                              <option value="100">100</option>
                              <option value="500">500</option>
                              <option value="1000">1000</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <Pagination
                        pageNo={pageNo}
                        pageSize={pageSize}
                        totalRecords={totalRecords}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </div>
                )}
                  </>
                ) : (
                  <>
                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <div className="card border-0 bg-light">
                          <div className="card-body py-2">
                            <div className="small text-muted">
                              Total receivable (ledger net)
                            </div>
                            <div className="fw-bold">{formatMoney(totalsNetAll.receiveable)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card border-0 bg-light">
                          <div className="card-body py-2">
                            <div className="small text-muted">
                              Total received (ledger net)
                            </div>
                            <div className="fw-bold text-success">{formatMoney(totalsNetAll.received)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card border-0 bg-light">
                          <div className="card-body py-2">
                            <div className="small text-muted">
                              Outstanding (ledger net)
                            </div>
                            <div className="fw-bold text-warning">{formatMoney(totalsNetAll.pending)}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <p className="small text-muted mb-2">
                      Summary totals match the AD ledger net figures (latest row
                      per fee), not the sum of challan due amounts, so older unpaid
                      amounts that were rolled into a later challan are not counted
                      twice.
                    </p>
                    <table className="table table-theme">
                      <thead>
                        <tr>
                          <th colSpan="8" className="text-end">
                            {challans.length} challan(s) found
                          </th>
                        </tr>
                        <tr>
                          <th>Month</th>
                          <th>Year</th>
                          <th>Challan No</th>
                          <th>Dated</th>
                          <th>Due Date</th>
                          <th>Due Amount</th>
                          <th>Status</th>
                          <th>Received Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingChallans ? (
                          <tr>
                            <td colSpan="8" className="text-center">
                              <Spinner animation="border" role="status" />
                            </td>
                          </tr>
                        ) : challans.length === 0 ? (
                          <tr>
                            <td colSpan="8">
                              <h4
                                className="mt-3"
                                style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}
                              >
                                No challan data found
                              </h4>
                            </td>
                          </tr>
                        ) : (
                          <>
                            {challans.map((item, index) => {
                              const dueAmount =
                                parseMoney(item.After_Due_Date_Amount) > 0
                                  ? parseMoney(item.After_Due_Date_Amount)
                                  : parseMoney(item.Due_Date_Amount);
                              const receivedAmount = item.Is_Paid ? dueAmount : 0;
                              return (
                                <tr key={`${item.Challan_No || "ch"}-${index}`}>
                                  <td>{item.Month}</td>
                                  <td>{item.Year}</td>
                                  <td>{item.Challan_No}</td>
                                  <td>{item.Dated ? moment(item.Dated).format("DD/MM/YYYY") : ""}</td>
                                  <td>{item.Due_Date ? moment(item.Due_Date).format("DD/MM/YYYY") : ""}</td>
                                  <td>{formatMoney(dueAmount)}</td>
                                  <td>
                                    <span className={`badge ${item.Is_Paid ? "bg-success" : "bg-warning text-dark"}`}>
                                      {item.Is_Paid ? "Paid" : "Unpaid"}
                                    </span>
                                  </td>
                                  <td>{formatMoney(receivedAmount)}</td>
                                </tr>
                              );
                            })}
                            <tr className="table-light">
                              <td colSpan="5" className="text-end small text-muted">
                                <b>Net totals (ledger)</b>
                              </td>
                              <td>
                                <b>{formatMoney(totalsNetAll.receiveable)}</b>
                              </td>
                              <td className="small text-muted">
                                <b>{formatMoney(totalsNetAll.pending)}</b> due
                              </td>
                              <td>
                                <b>{formatMoney(totalsNetAll.received)}</b>
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default StudentLedger2;
